import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Store} from '@ngxs/store';
import {WINDOW_TOKEN} from '../core/tokens/window.token';
import {OAuthConfig, TokenResponse} from './oauth-config';
import {catchError, Observable, tap, throwError} from 'rxjs';
import * as AuthStateActions from './+state/actions';
import {AuthorizationError, InvalidStateError, NoAuthorizationCodeError, NoVerifierError} from './errors';

@Injectable({
  providedIn: 'root'
})
export class OauthPkceService {
  private readonly STATE_KEY = 'pkce_state';
  private readonly VERIFIER_KEY = 'pkce_code_verifier';
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly window = inject(WINDOW_TOKEN);

  private generateRandomString(): string {
    const array = new Uint32Array(32);
    this.window.crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');
  }

  private async sha256(plain: string): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return await this.window.crypto.subtle.digest('SHA-256', data);
  }

  private base64UrlEncoder(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const str = String.fromCharCode.apply(null, Array.from(bytes));
    return this.window.btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async generatePkceChallenge(verifier: string): Promise<string> {
    const hash = await this.sha256(verifier);
    return this.base64UrlEncoder(hash);
  }

  private parseQueryString(q: string): Record<string, string> {
    if (!q) return {};

    const params: Record<string, string> = {};
    const segments = q.split('&');

    for (const segment of segments) {
      const [key, value] = segment.split('=');
      if (key) {
        params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
      }
    }

    return params;
  }

  async initiateAuthentication(config: OAuthConfig): Promise<void> {
    const state = this.generateRandomString();
    this.window.sessionStorage.setItem(this.STATE_KEY, state);

    const codeVerifier = this.generateRandomString();
    this.window.sessionStorage.setItem(this.VERIFIER_KEY, codeVerifier);

    const codeChallenge = await this.generatePkceChallenge(codeVerifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.clientId,
      state,
      scope: config.requestedScopes,
      redirect_uri: config.redirectUri,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });

    const authUrl = `${config.authorizationEndpoint}?${params.toString()}`;

    this.window.location.assign(authUrl);
  }

  handleCallback(config: OAuthConfig): Observable<TokenResponse> {
    const queryString: string = this.window.location.search.substring(1);
    const params: Record<string, string> = this.parseQueryString(queryString);

    // Clear URL parameters immediately after parsing to avoid re-processing and flashing the code
    try {
      this.window.history.replaceState({}, this.window.document.title, this.window.location.pathname);
    } catch {
      // ignore if history API is unavailable (e.g., test environment)
    }

    // Check for error
    if (params['error']) {
      this.cleanup();
      const error = new AuthorizationError(
        params['error'],
        params['error_description'] || 'Authorization failed'
      );
      this.store.dispatch(new AuthStateActions.LoginFailure(error));
      return throwError(() => error);
    }

    // Check for authorization code
    if (!params['code']) {
      const error = new NoAuthorizationCodeError('No authorization code received');
      this.store.dispatch(new AuthStateActions.LoginFailure(error));
      return throwError(() => error);
    }

    // Verify state
    const storedState: string | null = sessionStorage.getItem(this.STATE_KEY);
    if (storedState !== params['state']) {
      this.cleanup();
      const error = new InvalidStateError('State mismatch - possible CSRF attack');
      this.store.dispatch(new AuthStateActions.LoginFailure(error));
      return throwError(() => error);
    }

    // Get stored code verifier
    const codeVerifier: string | null = sessionStorage.getItem(this.VERIFIER_KEY);
    if (!codeVerifier) {
      this.cleanup();
      const error = new NoVerifierError('Code verifier not found');
      this.store.dispatch(new AuthStateActions.LoginFailure(error));
      return throwError(() => error);
    }

    // Exchange code for token
    return this.exchangeCodeForToken(config, params['code'], codeVerifier);
  }

  /**
   * Exchange authorization code for access token
   */
  private exchangeCodeForToken(
    config: OAuthConfig,
    code: string,
    codeVerifier: string
  ): Observable<TokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('client_id', config.clientId)
      .set('redirect_uri', config.redirectUri)
      .set('code_verifier', codeVerifier);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.httpClient.post<TokenResponse>(config.tokenEndpoint, body.toString(), { headers }).pipe(
      tap(response => {
        // Clean up URL and storage
        this.cleanup();
        this.window.history.replaceState({}, this.window.document.title, this.window.location.pathname);

        // Store ID token for logout if present
        if (response.id_token) {
          this.window.sessionStorage.setItem('id_token', response.id_token);
        }

        // Update NGXS state
        this.store.dispatch(new AuthStateActions.LoginSuccess(
          response.access_token,
          response.refresh_token || null,
          response.expires_in || null,
          response.token_type || 'Bearer',
          response.scope || null
        ));
      }),
      catchError(error => {
        this.cleanup();
        const authError = new AuthorizationError(
          error?.error?.error || 'token_exchange_failed',
          error?.error?.error_description || 'Failed to exchange code for token'
        );
        this.store.dispatch(new AuthStateActions.LoginFailure(authError));
        return throwError(() => authError);
      })
    );
  }

  /**
   * Refresh access token using refresh token
   */
  refreshAccessToken(config: OAuthConfig, refreshToken: string): Observable<TokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken)
      .set('client_id', config.clientId);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.httpClient.post<TokenResponse>(config.tokenEndpoint, body.toString(), { headers }).pipe(
      tap(response => {
        this.store.dispatch(new AuthStateActions.RefreshTokenSuccess(
          response.access_token,
          response.refresh_token || null,
          response.expires_in || null
        ));
      }),
      catchError(error => {
        const authError = new AuthorizationError(
          error?.error?.error || 'refresh_failed',
          error?.error?.error_description || 'Failed to refresh token'
        );
        this.store.dispatch(new AuthStateActions.RefreshTokenFailure(authError));
        return throwError(() => authError);
      })
    );
  }

  /**
   * Cleanup temporary storage
   */
  private cleanup(): void {
    this.window.sessionStorage.removeItem(this.STATE_KEY);
    this.window.sessionStorage.removeItem(this.VERIFIER_KEY);
  }

  /**
   * Cleanup all auth-related storage including ID token
   */
  private cleanupAll(): void {
    this.cleanup();
    this.window.sessionStorage.removeItem('id_token');
  }

  /**
   * Logout user locally and from IdP
   */
  logout(config: OAuthConfig): void {
    // Get the current ID token if available for logout hint (before cleaning up)
    const idToken = this.window.sessionStorage.getItem('id_token');
    
    // Clear local state first
    this.cleanupAll();
    this.store.dispatch(new AuthStateActions.Logout());
    
    // Construct logout URL with proper parameters
    const logoutParams = new URLSearchParams({
      client_id: config.clientId,
      post_logout_redirect_uri: config.postLogoutRedirectUri
    });
    
    // Add ID token hint if available (recommended for proper session termination)
    if (idToken) {
      logoutParams.set('id_token_hint', idToken);
    }
    
    const logoutUrl = `${config.logoutEndpoint}?${logoutParams.toString()}`;
    
    // Redirect to IdP logout endpoint
    this.window.location.assign(logoutUrl);
  }

  /**
   * Logout user locally only (for cases where IdP logout is not needed)
   */
  logoutLocal(): void {
    this.cleanupAll();
    this.store.dispatch(new AuthStateActions.Logout());
  }
}
